import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import * as userService from '../../services/userService';
import { UsageStatsCard, PostpaidAccountCard, UnitCard } from '../../components/cards';

function PostpaidUserDashboard() {
  const { user, unit: authUnit, hasRole, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [debugMode, setDebugMode] = useState(false);
  const [unit, setUnit] = useState(null);
  const [stats, setStats] = useState({
    currentBill: {
      period: 'October 1 - October 31, 2023',
      status: 'Active'
    },
    usage: {
      today: 0,
      thisWeek: 0,
      thisMonth: 0
    },
    usageHistory: []
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Toggle debug mode
  const toggleDebugMode = () => {
    setDebugMode(prev => !prev);
  };

  // Check if user has any staff role
  const isStaff = user && (hasRole('ADMIN') || hasRole('MANAGEMENT') || hasRole('OPERATOR'));

  // Redirect staff users directly to admin dashboard
  if (user && isStaff && !authLoading) {
    return <Navigate to="/admin" replace />;
  }

  // Fetch unit information using endpoint
  useEffect(() => {
    const fetchUnitInfo = async () => {
      if (user && user.siteUuid && user.unitId) {
        try {
          const unitInfo = await userService.getUnitByUnitId(user.siteUuid, user.unitId);
          if (unitInfo) {
            setUnit(unitInfo);
            // console.log('Unit information fetched:', unitInfo);
          }
        } catch (error) {
          // console.error('Failed to fetch unit information:', error);
        }
      } else if (authUnit) {
        setUnit(authUnit);
      }
    };

    fetchUnitInfo();
  }, [user, authUnit]);

  // Simulate loading billing and usage data
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Simulate API call with mock data
    const fetchData = async () => {
      try {
        // In a real implementation, you would fetch actual postpaid data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Modified mock data to focus on usage only, not payment information
        setStats({
          currentBill: {
            period: 'October 1 - October 31, 2023',
            status: 'Active'
          },
          usage: {
            today: 12.5,
            thisWeek: 87.3,
            thisMonth: 320.6
          },
          usageHistory: [
            {
              id: 'record-2023-09',
              date: 'September 30, 2023',
              period: 'September 2023',
              usage: 685.2,
              status: 'Completed'
            },
            {
              id: 'record-2023-08',
              date: 'August 31, 2023',
              period: 'August 2023',
              usage: 720.1,
              status: 'Completed'
            },
            {
              id: 'record-2023-07',
              date: 'July 31, 2023',
              period: 'July 2023',
              usage: 756.9,
              status: 'Completed'
            }
          ]
        });
      } catch (error) {
        // console.error('Error fetching postpaid account data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Show loading indicator if auth is still loading
  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <div className="ml-4 text-lg font-medium">Loading authentication...</div>
      </div>
    );
  }

  // Show login prompt if user is not authenticated
  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-2xl font-bold mb-4">Please log in to view your dashboard</h2>
        <div className="flex space-x-4">
          <Link to="/login" className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded">
            Sign In
          </Link>
          <Link to="/register" className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded">
            Create Account
          </Link>
        </div>
      </div>
    );
  }

  // Show loading indicator for dashboard data
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <div className="ml-4 text-lg font-medium">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-hooray-red font-mont">Welcome, {user.firstName || user.name || 'User'}!</h1>
        <p className="text-hooray-red">Here's an overview of your Hooray Power account.</p>
        
        {/* Debug button - temporarily commented out
        <button 
          onClick={toggleDebugMode}
          className={`mt-4 px-4 py-2 rounded ${debugMode ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-500 hover:bg-gray-600'} text-white`}
        >
          {debugMode ? 'Hide Debug' : 'Debug'}
        </button>
        */}
      </div>
      
      {/* Debug section */}
      {debugMode && (
        <div className="mb-8 border-2 border-gray-300 p-6 rounded-lg">
          <h2 className="text-2xl font-bold mb-4">Debug: User Account Information</h2>
          
          <div className="bg-gray-100 p-4 rounded-lg">
            <h3 className="text-lg font-bold mb-2">User Information:</h3>
            <div className="bg-white p-4 rounded overflow-auto">
              <p><strong>Account UUID:</strong> {user?.uuid || 'Not available'}</p>
              <p><strong>User ID:</strong> {user?.id || 'Not available'}</p>
              <p><strong>Email:</strong> {user?.email || 'Not available'}</p>
              <p><strong>Name:</strong> {user?.firstName} {user?.lastName} {user?.name || ''}</p>
              <p><strong>Roles:</strong> {user?.roles?.join(', ') || 'None'}</p>
              <p><strong>Site Name:</strong> {user?.siteName || 'Not available'}</p>
              <p><strong>Site UUID:</strong> {user?.siteUuid || 'Not available'}</p>
              <p><strong>Unit ID:</strong> {user?.unitId !== undefined ? user.unitId : 'Not available'}</p>
              <p><strong>Full User Object:</strong></p>
              <pre className="text-xs mt-2 bg-gray-100 p-2 rounded overflow-x-auto">
                {JSON.stringify(user, null, 2)}
              </pre>
            </div>
          </div>
          
          <div className="bg-gray-100 p-4 rounded-lg mt-4">
            <h3 className="text-lg font-bold mb-2">Unit Information:</h3>
            <div className="bg-white p-4 rounded overflow-auto">
              {unit ? (
                <>
                  <p><strong>Unit Number:</strong> {unit?.unitNumber || 'Not available'}</p>
                  <p><strong>Site Name:</strong> {unit?.siteName || 'Not available'}</p>
                  <p><strong>Is Prepaid:</strong> {unit?.isPrepaid ? 'Yes' : 'No'}</p>
                  <p><strong>Full Unit Object:</strong></p>
                  <pre className="text-xs mt-2 bg-gray-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify(unit, null, 2)}
                  </pre>
                </>
              ) : (
                <p className="text-red-500 font-medium">No unit data available.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Account Information Card - Using PostpaidAccountCard component */}
        <PostpaidAccountCard 
          status={stats.currentBill.status}
          billingPeriod={stats.currentBill.period}
        />

        {/* Usage Card - Using the UsageStatsCard component
        <UsageStatsCard 
          usage={stats.usage}
          showViewDataLink={true}
        /> */}

        {/* Unit Information Card - Using the UnitCard component */}
        <UnitCard unit={unit} isPrepaid={false} />
      </div>

      {/* Usage History - Replacing Billing History */}
      {/* 
      <div className="card bg-white shadow-md rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Usage History</h2>
          <button className="text-primary hover:text-primary-dark text-sm font-medium">
            View All →
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Record ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage (kWh)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stats.usageHistory && stats.usageHistory.map((record) => (
                <tr key={record.id}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {record.id}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {record.date}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {record.period}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {record.usage.toFixed(1)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      record.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {record.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      */}
    </div>
  );
}

export default PostpaidUserDashboard; 